import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { API_IP } from "../../env";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import "./ProviderDetailForm.css";

// ─── Constants ──────────────────────────────────────────────────────────────
const TABS = [
  { id: "profile", label: "Profile", icon: "bi-person" },
  { id: "contact", label: "Contact", icon: "bi-envelope" },
  { id: "hours", label: "Hours", icon: "bi-clock" },
  { id: "bio", label: "Bio", icon: "bi-file-text" },
  { id: "services", label: "Services", icon: "bi-card-checklist" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatHour(h) {
  if (h === null || h === undefined || h === "") return "";
  const n = Number(h);
  if (Number.isNaN(n)) return String(h);
  const period = n >= 12 ? "PM" : "AM";
  const display = n % 12 === 0 ? 12 : n % 12;
  return `${display}:00 ${period}`;
}

function periodOf(h) {
  const n = Number(h);
  if (Number.isNaN(n)) return "";
  return n >= 12 ? "PM" : "AM";
}

function validateField(name, value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    if (["provider_name", "email", "phone_number"].includes(name)) {
      return "Required";
    }
    return null;
  }
  if (name === "email" && !EMAIL_RE.test(value)) {
    return "Enter a valid email address";
  }
  if (name === "phone_number") {
    const digits = String(value).replace(/\D/g, "");
    if (digits.length < 7) return "Phone number is too short";
  }
  if (name === "opening_hour" || name === "closing_hour") {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0 || n > 23) return "Use 0–23";
  }
  return null;
}

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// ─── Field components ──────────────────────────────────────────────────────
function ReadField({ label, value, full }) {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className={`pdf-field ${full ? "pdf-grid__full" : ""}`}>
      <span className="pdf-field__label">{label}</span>
      <div className={`pdf-field__value ${isEmpty ? "pdf-field__value--empty" : ""}`}>
        {isEmpty ? "—" : value}
      </div>
    </div>
  );
}

function EditField({
  label,
  name,
  value,
  type = "text",
  autoComplete,
  onChange,
  onBlur,
  error,
  multiline,
  rows = 4,
  hint,
  full,
}) {
  const id = `pdf-${name}`;
  return (
    <div className={`pdf-field ${error ? "pdf-field--invalid" : ""} ${full ? "pdf-grid__full" : ""}`}>
      <label htmlFor={id} className="pdf-field__label">{label}</label>
      {multiline ? (
        <textarea
          id={id}
          name={name}
          rows={rows}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className="pdf-field__textarea"
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          autoComplete={autoComplete}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className="pdf-field__input"
        />
      )}
      {error ? (
        <span id={`${id}-error`} className="pdf-field__error">
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true" /> {error}
        </span>
      ) : hint ? (
        <span className="pdf-field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

function HourEditField({ label, name, value, onChange, onBlur, error }) {
  const id = `pdf-${name}`;
  const suffix = value === "" || value === null || value === undefined ? "" : periodOf(value);
  return (
    <div className={`pdf-field ${error ? "pdf-field--invalid" : ""}`}>
      <label htmlFor={id} className="pdf-field__label">{label}</label>
      <div className="pdf-hour">
        <input
          id={id}
          name={name}
          type="number"
          min={0}
          max={23}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : `${id}-hint`}
          className="pdf-field__input"
        />
        <span className="pdf-hour__suffix" aria-hidden="true">{suffix || "—"}</span>
      </div>
      {error ? (
        <span id={`${id}-error`} className="pdf-field__error">
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true" /> {error}
        </span>
      ) : (
        <span id={`${id}-hint`} className="pdf-field__hint">24-hour format · 0–23</span>
      )}
    </div>
  );
}

// ─── Skeleton ──────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="pdf-skeleton" aria-busy="true" aria-live="polite">
      <div className="pdf-skeleton__hero">
        <div className="pdf-skeleton__avatar" />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="pdf-skeleton__bar" style={{ width: "55%", height: 18 }} />
          <div className="pdf-skeleton__bar" style={{ width: "35%" }} />
        </div>
      </div>
      <div className="pdf-skeleton__row">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="pdf-skeleton__field">
            <div className="pdf-skeleton__bar" style={{ width: "32%", height: 10 }} />
            <div className="pdf-skeleton__bar" style={{ width: "80%" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Confirm modal ─────────────────────────────────────────────────────────
function ConfirmModal({ action, providerName, onCancel, onConfirm, loading, error }) {
  if (!action) return null;

  const cfg = {
    verify: {
      icon: "bi-patch-check-fill",
      iconClass: "pdf-confirm__icon--success",
      title: `Verify ${providerName || "this provider"}?`,
      body: "This sends a verification email to the provider and marks them visible to clients.",
      confirmLabel: "Yes, verify",
      confirmClass: "pdf-btn--success",
    },
    unverify: {
      icon: "bi-shield-exclamation",
      iconClass: "pdf-confirm__icon--danger",
      title: `Unverify ${providerName || "this provider"}?`,
      body: "The provider will be hidden from clients until re-verified.",
      confirmLabel: "Yes, unverify",
      confirmClass: "pdf-btn--danger",
    },
    discard: {
      icon: "bi-exclamation-triangle-fill",
      iconClass: "pdf-confirm__icon--warn",
      title: "Discard unsaved changes?",
      body: "Your edits to this provider's details will be lost.",
      confirmLabel: "Discard",
      confirmClass: "pdf-btn--danger",
    },
  }[action];

  return (
    <Modal
      show
      onHide={loading ? () => { } : onCancel}
      size="sm"
      centered
      backdrop="static"
      contentClassName="pdf-confirm"
      aria-labelledby="pdf-confirm-title"
    >
      <Modal.Body>
        <div className="pdf-confirm__head">
          <div className={`pdf-confirm__icon ${cfg.iconClass}`}>
            <i className={`bi ${cfg.icon}`} aria-hidden="true" />
          </div>
          <h3 id="pdf-confirm-title" className="pdf-confirm__title">{cfg.title}</h3>
        </div>
        <div className="pdf-confirm__body">{cfg.body}</div>
        {error && (
          <div className="pdf-confirm__error" role="alert">
            <i className="bi bi-exclamation-circle-fill" aria-hidden="true" /> {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="pdf-footer" style={{ justifyContent: "flex-end" }}>
          <button
            type="button"
            className="pdf-btn pdf-btn--ghost"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`pdf-btn ${cfg.confirmClass}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <span className="pdf-btn__spinner" aria-hidden="true" /> : <i className="bi bi-check2" aria-hidden="true" />}
            {error ? "Retry" : cfg.confirmLabel}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
function ProviderDetailForm({ show, onHide, providerID, providerName, setRefreshProvider }) {
  const [providerData, setProviderData] = useState(null);
  const [editData, setEditData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [services, setServices] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [idCopied, setIdCopied] = useState(false);
  const tabsRef = useRef([]);

  const verified = providerData?.verification;

  async function req(url, method, data) {
    const token = localStorage.getItem("@uphealth_login_token");
    return await axios({ url, method, headers: { Authorization: `Bearer ${token}` }, data });
  }

  const fetchAll = useCallback(async () => {
    if (!providerID) return;
    setIsLoading(true);
    try {
      const { data } = await req(`${API_IP}/api/admin/providers/${providerID}/detail`, "get", []);
      setProviderData(data);
      setEditData(data);
    } catch (err) {
      console.error("Failed to fetch provider details", err);
      setIsLoading(false);
      onHide();
      return;
    }
    try {
      const { data } = await req(`${API_IP}/api/admin/providers/${providerID}`, "get", []);
      setServices(data.providerServices || []);
    } catch (err) {
      console.error("Failed to fetch provider services", err);
    }
    setIsLoading(false);
  }, [providerID, onHide]);

  useEffect(() => {
    if (show) {
      setIsEditing(false);
      setActiveTab("profile");
      setFieldErrors({});
      setTouched({});
      setConfirmAction(null);
      setActionError(null);
      setProviderData(null);
      setIsLoading(true);
      fetchAll();
    }
  }, [show, fetchAll]);

  const isDirty = useMemo(() => {
    if (!isEditing || !providerData) return false;
    return Object.keys(editData).some((k) => editData[k] !== providerData[k]);
  }, [editData, providerData, isEditing]);

  const visibleErrors = useMemo(() => {
    return Object.entries(fieldErrors).filter(([, v]) => v);
  }, [fieldErrors]);

  const hasBlockingErrors = visibleErrors.length > 0;

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const errs = {};
    ["provider_name", "email", "phone_number", "opening_hour", "closing_hour"].forEach((k) => {
      const e = validateField(k, editData[k]);
      if (e) errs[k] = e;
    });
    setFieldErrors(errs);
    setTouched((prev) => {
      const next = { ...prev };
      Object.keys(errs).forEach((k) => (next[k] = true));
      return next;
    });
    return Object.keys(errs).length === 0;
  };

  const requestClose = () => {
    if (isEditing && isDirty) {
      setConfirmAction("discard");
      return;
    }
    onHide();
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setFieldErrors({});
    setTouched({});
  };

  const handleCancelEdit = () => {
    if (isDirty) {
      setConfirmAction("discard");
      return;
    }
    setIsEditing(false);
    setEditData(providerData);
    setFieldErrors({});
    setTouched({});
  };

  const handleSave = async () => {
    if (!validateAll()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await req(`${API_IP}/api/admin/providers/${providerID}/detail`, "patch", editData);
      setProviderData(editData);
      setIsEditing(false);
      setRefreshProvider((v) => !v);
    } catch (err) {
      setActionError(err?.response?.data?.message || "Failed to save changes. Please try again.");
    }
    setActionLoading(false);
  };

  const performVerification = async (verification) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await req(`${API_IP}/api/admin/verification`, "patch", {
        provider_id: providerID,
        verification,
      });
      setRefreshProvider((v) => !v);
      setConfirmAction(null);
      onHide();
    } catch (err) {
      setActionError(
        err?.response?.data?.message || "Network error. Please try again."
      );
    }
    setActionLoading(false);
  };

  const handleConfirm = () => {
    if (confirmAction === "verify") return performVerification(true);
    if (confirmAction === "unverify") return performVerification(false);
    if (confirmAction === "discard") {
      setConfirmAction(null);
      setIsEditing(false);
      setEditData(providerData);
      setFieldErrors({});
      setTouched({});
      return;
    }
  };

  const handleCopyId = async () => {
    if (!providerID) return;
    try {
      await navigator.clipboard.writeText(providerID);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1500);
    } catch {
      // clipboard blocked; silently ignore
    }
  };

  const handleTabKey = (e, idx) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      const next = (idx + 1) % TABS.length;
      setActiveTab(TABS[next].id);
      tabsRef.current[next]?.focus();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const prev = (idx - 1 + TABS.length) % TABS.length;
      setActiveTab(TABS[prev].id);
      tabsRef.current[prev]?.focus();
    }
  };

  // ─── Render helpers ──────────────────────────────────────────────────────
  const renderProfilePanel = () => {
    if (isEditing) {
      return (
        <div className="pdf-grid">
          <EditField
            label="Provider Name"
            name="provider_name"
            value={editData.provider_name}
            autoComplete="name"
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.provider_name}
          />
          <EditField
            label="Title"
            name="title"
            value={editData.title}
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.title}
          />
          <EditField
            full
            label="Experience"
            name="experience"
            value={editData.experience}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={4}
            error={fieldErrors.experience}
            hint="Career summary, notable roles, training."
          />
        </div>
      );
    }
    return (
      <div className="pdf-grid">
        <ReadField label="Provider Name" value={providerData.provider_name} />
        <ReadField label="Title" value={providerData.title} />
        <ReadField full label="Experience" value={providerData.experience} />
      </div>
    );
  };

  const renderContactPanel = () => {
    if (isEditing) {
      return (
        <div className="pdf-grid">
          <EditField
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            value={editData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.email}
          />
          <EditField
            label="Phone Number"
            name="phone_number"
            type="tel"
            autoComplete="tel"
            value={editData.phone_number}
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.phone_number}
          />
          <EditField
            full
            label="Address"
            name="address"
            autoComplete="street-address"
            value={editData.address}
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.address}
          />
          <EditField
            label="City"
            name="city"
            autoComplete="address-level2"
            value={editData.city}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <EditField
            label="Province"
            name="province"
            autoComplete="address-level1"
            value={editData.province}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <EditField
            full
            label="Country"
            name="country"
            autoComplete="country-name"
            value={editData.country}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
      );
    }
    return (
      <div className="pdf-grid">
        <ReadField label="Email" value={providerData.email} />
        <ReadField label="Phone Number" value={providerData.phone_number} />
        <ReadField full label="Address" value={providerData.address} />
        <ReadField label="City" value={providerData.city} />
        <ReadField label="Province" value={providerData.province} />
        <ReadField full label="Country" value={providerData.country} />
      </div>
    );
  };

  const renderHoursPanel = () => {
    if (isEditing) {
      return (
        <div className="pdf-grid">
          <HourEditField
            label="Open Hour"
            name="opening_hour"
            value={editData.opening_hour}
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.opening_hour}
          />
          <HourEditField
            label="Close Hour"
            name="closing_hour"
            value={editData.closing_hour}
            onChange={handleChange}
            onBlur={handleBlur}
            error={fieldErrors.closing_hour}
          />
        </div>
      );
    }
    return (
      <div className="pdf-grid">
        <ReadField label="Open Hour" value={formatHour(providerData.opening_hour)} />
        <ReadField label="Close Hour" value={formatHour(providerData.closing_hour)} />
      </div>
    );
  };

  const renderBioPanel = () => {
    if (isEditing) {
      return (
        <div className="pdf-grid pdf-grid--single">
          <EditField
            label="Credentials"
            name="credentials"
            value={editData.credentials}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={3}
            hint="License numbers, certifications, regulatory bodies."
          />
          <EditField
            label="Self Description"
            name="self_description"
            value={editData.self_description}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={6}
            hint="Public-facing biography displayed on the provider's profile."
          />
        </div>
      );
    }
    return (
      <div className="pdf-grid pdf-grid--single">
        <ReadField label="Credentials" value={providerData.credentials} />
        <ReadField label="Self Description" value={providerData.self_description} />
      </div>
    );
  };

  const renderServicesPanel = () => {
    if (services.length === 0) {
      return (
        <div className="pdf-empty">
          <i className="bi bi-clipboard-x" aria-hidden="true" />
          <div className="pdf-empty__text">No services assigned</div>
        </div>
      );
    }
    return (
      <div className="pdf-service-list">
        {services.map((s) => (
          <div key={s.care_service_id} className="pdf-service-chip">
            <span className="pdf-service-chip__name">{s.care_service_name}</span>
            <span className="pdf-service-chip__price">
              ${s.hourly_rate ?? s.price ?? "—"}/hr
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderActivePanel = () => {
    switch (activeTab) {
      case "profile": return renderProfilePanel();
      case "contact": return renderContactPanel();
      case "hours": return renderHoursPanel();
      case "bio": return renderBioPanel();
      case "services": return renderServicesPanel();
      default: return null;
    }
  };

  // ─── Header ──────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <div className="pdf-header">
      <div className={`pdf-header__avatar${isLoading ? " pdf-header__avatar--loading" : ""}`} aria-hidden="true">
        {providerData?.img_url
          ? <img src={providerData.img_url} alt="" />
          : initials(providerName || providerData?.provider_name)}
      </div>
      <div className="pdf-header__content">
        <h2 id="pdf-modal-title" className="pdf-header__title">
          {isEditing ? "Editing profile" : (providerData?.provider_name || providerName || "Provider")}
        </h2>
        {(providerData?.title || isEditing) && (
          <div id="pdf-modal-subtitle" className="pdf-header__subtitle">
            {providerData?.title || "—"}
          </div>
        )}
        <div className="pdf-header__meta">
          {providerData && (
            <span
              className={`pdf-status-pill ${verified ? "pdf-status-pill--verified" : "pdf-status-pill--pending"}`}
            >
              {verified ? "Verified" : "Pending"}
            </span>
          )}
          {providerID && (
            <button
              type="button"
              onClick={handleCopyId}
              className={`pdf-id-chip ${idCopied ? "pdf-id-chip--copied" : ""}`}
              title="Copy provider ID"
              aria-label="Copy provider ID"
            >
              <i className={`bi ${idCopied ? "bi-check2" : "bi-hash"}`} aria-hidden="true" />
              {String(providerID).slice(0, 6)}
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        className="pdf-close"
        onClick={requestClose}
        aria-label="Close"
      >
        <i className="bi bi-x-lg" aria-hidden="true" />
      </button>
      {idCopied && <div className="pdf-toast">ID copied</div>}
    </div>
  );

  // ─── Tabs ────────────────────────────────────────────────────────────────
  const renderTabs = () => (
    <div className="pdf-tabs" role="tablist" aria-label="Provider sections">
      {TABS.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => (tabsRef.current[i] = el)}
          role="tab"
          id={`pdf-tab-${t.id}`}
          aria-selected={activeTab === t.id}
          aria-controls={`pdf-panel-${t.id}`}
          tabIndex={activeTab === t.id ? 0 : -1}
          onClick={() => setActiveTab(t.id)}
          onKeyDown={(e) => handleTabKey(e, i)}
          className={`pdf-tab ${activeTab === t.id ? "pdf-tab--active" : ""}`}
        >
          <i className={`bi ${t.icon}`} aria-hidden="true" /> {t.label}
        </button>
      ))}
    </div>
  );

  // ─── Footer ──────────────────────────────────────────────────────────────
  const renderFooter = () => (
    <div className="pdf-footer">
      <button
        type="button"
        className="pdf-btn pdf-btn--ghost"
        onClick={isEditing ? handleCancelEdit : requestClose}
        disabled={actionLoading}
      >
        {isEditing ? "Cancel" : "Close"}
      </button>

      <div className="pdf-footer__right">
        {!isLoading && providerData && (
          isEditing ? (
            <button
              type="button"
              className="pdf-btn pdf-btn--primary"
              onClick={handleSave}
              disabled={actionLoading || hasBlockingErrors || !isDirty}
            >
              {actionLoading ? (
                <span className="pdf-btn__spinner" aria-hidden="true" />
              ) : (
                <i className="bi bi-check2" aria-hidden="true" />
              )}
              Save changes
            </button>
          ) : (
            <>
              <button
                type="button"
                className="pdf-btn pdf-btn--outline"
                onClick={handleStartEdit}
              >
                <i className="bi bi-pencil" aria-hidden="true" /> Edit details
              </button>
              {verified ? (
                <button
                  type="button"
                  className="pdf-btn pdf-btn--danger-outline"
                  onClick={() => { setActionError(null); setConfirmAction("unverify"); }}
                >
                  <i className="bi bi-shield-slash" aria-hidden="true" /> Unverify
                </button>
              ) : (
                <button
                  type="button"
                  className="pdf-btn pdf-btn--success"
                  onClick={() => { setActionError(null); setConfirmAction("verify"); }}
                >
                  <i className="bi bi-patch-check-fill" aria-hidden="true" /> Verify
                </button>
              )}
            </>
          )
        )}
      </div>
    </div>
  );

  // ─── Errors banner (edit mode, top of active panel) ─────────────────────
  const renderErrorsBanner = () => {
    if (!isEditing || visibleErrors.length < 2) return null;
    return (
      <div className="pdf-errors-banner" role="alert">
        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
        <div>
          <strong>Please fix {visibleErrors.length} field{visibleErrors.length === 1 ? "" : "s"}:</strong>
          <ul className="pdf-errors-banner__list">
            {visibleErrors.map(([field, msg]) => (
              <li key={field}>
                <a
                  href={`#pdf-${field}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`pdf-${field}`)?.focus();
                  }}
                >
                  {field.replace(/_/g, " ")}: {msg}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderSaveError = () => {
    if (!actionError || confirmAction) return null;
    return (
      <div className="pdf-errors-banner" role="alert">
        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
        <div>{actionError}</div>
      </div>
    );
  };

  return (
    <>
      <Modal
        show={show}
        onHide={requestClose}
        size="lg"
        centered
        backdrop="static"
        contentClassName="pdf-modal"
        aria-labelledby="pdf-modal-title"
        aria-describedby="pdf-modal-subtitle"
      >
        {renderHeader()}

        {isLoading || !providerData ? (
          <Skeleton />
        ) : (
          <>
            {renderTabs()}
            <div
              role="tabpanel"
              id={`pdf-panel-${activeTab}`}
              aria-labelledby={`pdf-tab-${activeTab}`}
              tabIndex={0}
              className="pdf-panel"
            >
              {renderErrorsBanner()}
              {renderSaveError()}
              {renderActivePanel()}
            </div>
          </>
        )}

        <Modal.Footer>{renderFooter()}</Modal.Footer>
      </Modal>

      <ConfirmModal
        action={confirmAction}
        providerName={providerData?.provider_name || providerName}
        onCancel={() => { setConfirmAction(null); setActionError(null); }}
        onConfirm={handleConfirm}
        loading={actionLoading}
        error={actionError}
      />
    </>
  );
}

export default ProviderDetailForm;
